import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * `app-otp-input` — componente compartilhado do código de confirmação OTP
 * (OTP-F-01, `ux.md` §4).
 *
 * 6 `<input maxlength="1">` REAIS — não é um input disfarçado em caixinhas
 * via CSS. É o que faz paste e autofill funcionarem sem truque visual.
 *
 * Contrato (imperativo, não `ControlValueAccessor` — 6 inputs nativos com
 * auto-advance/paste/auto-submit não ganham nada de `NgControl`; o pai lê o
 * código via `getCode()` e controla limpeza via `clear()`):
 * - `(codeComplete)` dispara UMA VEZ, ~150ms depois de completar as 6
 *   células (auto-submit) — o pai não precisa reimplementar o debounce.
 * - `getCode()` devolve o código atual se completo, `null` caso contrário —
 *   é o ÚNICO sinal de "6/6 preenchidas?" (usado pelo clique manual em
 *   "Confirmar" e pelo `[disabled]` do botão no template do pai). Não existe
 *   um `(filled)` paralelo: duas fontes pra mesma decisão divergem.
 * - `clear()` — usado pelo pai no estado "código errado"; já devolve o
 *   foco à célula 1 (ux.md §6: células limpas, foco na célula 1).
 */
@Component({
  standalone: true,
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule]
})
export class OtpInputComponent implements OnChanges, OnDestroy, AfterViewInit, AfterViewChecked {
  @Input() length = 6;
  @Input() disabled = false;
  @Input() invalid = false;
  /** Flash de confirmação (ux.md §5.11) — borda verde, sem pulso. */
  @Input() success = false;

  /** Dispara uma vez, ~150ms após a 6ª célula ser preenchida (auto-submit). */
  @Output() codeComplete = new EventEmitter<string>();
  /**
   * Dispara a cada digitação/paste real do usuário (nunca em `clear()`
   * programático) — usado pelo pai só para saber "a pessoa recomeçou a
   * digitar", ex.: tirar a borda vermelha do estado "código incorreto"
   * sem apagar esse estado no instante do próprio `clear()`.
   */
  @Output() userInput = new EventEmitter<void>();

  @ViewChildren('cell') private cellRefs!: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  readonly indices: number[] = [];

  private autoSubmitHandle: ReturnType<typeof setTimeout> | undefined;
  private focusPending: number | null = null;

  constructor(private readonly cdr: ChangeDetectorRef) {
    this.rebuildCells();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['length']) {
      this.rebuildCells();
    }
  }

  ngOnDestroy(): void {
    this.cancelAutoSubmit();
  }

  ngAfterViewInit(): void {
    this.focusCell(0);
  }

  ngAfterViewChecked(): void {
    if (this.focusPending !== null) {
      const idx = this.focusPending;
      this.focusPending = null;
      this.cellRefs?.get(idx)?.nativeElement.focus();
    }
  }

  trackByIndex(i: number): number {
    return i;
  }

  onInput(event: Event, index: number): void {
    if (this.disabled) return;

    const input = event.target as HTMLInputElement;
    const digit = input.value.replace(/\D/g, '').slice(-1);
    this.digits[index] = digit;
    input.value = digit;

    if (digit && index < this.length - 1) {
      this.focusCell(index + 1);
    }

    this.userInput.emit();
    this.emitChange();
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    if (this.disabled) return;
    if (event.key !== 'Backspace') return;

    if (!this.digits[index] && index > 0) {
      event.preventDefault();
      this.digits[index - 1] = '';
      this.focusCell(index - 1);
      this.userInput.emit();
      this.emitChange();
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (this.disabled) return;
    event.preventDefault();

    const text = event.clipboardData?.getData('text') ?? '';
    const nums = text.replace(/\D/g, '').slice(0, this.length).split('');
    if (nums.length === 0) return;

    this.digits = new Array(this.length).fill('');
    nums.forEach((n, i) => (this.digits[i] = n));

    const nextFocus = nums.length < this.length ? nums.length : this.length - 1;
    this.focusCell(nextFocus);
    this.userInput.emit();
    this.emitChange();
  }

  /** Código atual se as `length` células estiverem preenchidas, senão `null`. */
  getCode(): string | null {
    return this.isComplete() ? this.digits.join('') : null;
  }

  /** Limpa as células e devolve o foco à célula 1 (ux.md §6, "código errado"). */
  clear(): void {
    this.cancelAutoSubmit();
    this.digits = new Array(this.length).fill('');
    this.focusCell(0);
    this.cdr.markForCheck();
  }

  private isComplete(): boolean {
    return this.digits.length === this.length && this.digits.every((d) => d !== '');
  }

  private emitChange(): void {
    this.cdr.markForCheck();

    if (this.isComplete()) {
      this.scheduleAutoSubmit();
    } else {
      this.cancelAutoSubmit();
    }
  }

  private scheduleAutoSubmit(): void {
    this.cancelAutoSubmit();
    this.autoSubmitHandle = setTimeout(() => {
      this.autoSubmitHandle = undefined;
      if (this.isComplete()) {
        this.codeComplete.emit(this.digits.join(''));
      }
    }, 150);
  }

  private cancelAutoSubmit(): void {
    if (this.autoSubmitHandle !== undefined) {
      clearTimeout(this.autoSubmitHandle);
      this.autoSubmitHandle = undefined;
    }
  }

  private focusCell(index: number): void {
    this.focusPending = index;
  }

  private rebuildCells(): void {
    this.digits = new Array(this.length).fill('');
    this.indices.length = 0;
    for (let i = 0; i < this.length; i++) {
      this.indices.push(i);
    }
  }
}
